import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { useFormik } from "formik";
import CustomStepper from "../../pages/dashboard/route-planner/build/stepper";
import { toFormikValidationSchema } from "zod-formik-adapter";
import {
  adminCreateLocationWithCustomerReqDtoSchema,
  useAdminLocationV2ControllerCreateLocationWithCustomer,
} from "service";
import useAddressAutocomplete from "../../hooks/use-autocomplete";
import {
  numericFieldSx,
  numericInputProps,
} from "@/shared/utils/numeric-input";
import { z } from "zod";

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  initialSearchedAddress?: string;
  onCustomerCreated?: (newLocationData?: {
    id: string;
    latitude: number;
    longitude: number;
    address: string;
  }) => void;
}

const steps = [1, 2];

// Modify the schema to allow empty string for serviceTime and convert it to 0, but require minimum of 1
const formValidationSchema = adminCreateLocationWithCustomerReqDtoSchema.extend(
  {
    serviceTime: z
      .preprocess(
        (val) => (val === "" ? 0 : val),
        z.number().min(1, "Service duration must be at least 1 minute"),
      )
      .optional(),
  },
);

const AddCustomerModal = ({
  open,
  onClose,
  initialSearchedAddress,
  onCustomerCreated,
}: AddCustomerModalProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [addressNeedsConfirmation, setAddressNeedsConfirmation] =
    useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const theme = useTheme();

  const { placePredictions, getPlacePredictions } = useAddressAutocomplete();

  const { mutate } = useAdminLocationV2ControllerCreateLocationWithCustomer({
    onSuccess: (response) => {
      const locationData = response.data;
      if (locationData) {
        onCustomerCreated?.({
          id: locationData.id,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          address: locationData.address,
        });
      } else {
        onCustomerCreated?.();
      }
      handleClose();
    },
  });

  // Single form with all data
  const form = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: initialSearchedAddress || "",
      serviceTime: "" as number | "",
      notes: "",
    },
    validationSchema: toFormikValidationSchema(formValidationSchema),
    onSubmit: (values) => {
      if (activeStep === 0) {
        setActiveStep(1);
      } else {
        const body = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email.toLowerCase(),
          address: values.address,
          serviceTime: values.serviceTime === "" ? 0 : values.serviceTime,
          notes: values.notes,
          ...(values.phone?.trim() && { phone: `+${values.phone.trim()}` }),
        };

        mutate({ body });
      }
    },
  });

  // Update address when initialSearchedAddress changes
  useEffect(() => {
    if (initialSearchedAddress) {
      form.setFieldValue("address", initialSearchedAddress);
      setAddressNeedsConfirmation(true);
      // Automatically trigger address suggestions when we move to step 1
      if (activeStep === 1) {
        getPlacePredictions({
          input: initialSearchedAddress,
        });
      }
    }
  }, [initialSearchedAddress, activeStep]); // Added activeStep to dependencies

  // Open autocomplete when predictions are available
  useEffect(() => {
    if (placePredictions.length > 0 && activeStep === 1) {
      setAutocompleteOpen(true);
    }
  }, [placePredictions, activeStep]);

  const handleClose = () => {
    // Reset form and confirmation state
    setActiveStep(0);
    setAddressNeedsConfirmation(false);
    setAutocompleteOpen(false);
    form.resetForm();
    onClose();
  };

  const handleNext = () => {
    form.submitForm();
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow going back to step 0 from step 1
    // Don't allow skipping ahead to step 1 if step 0 is incomplete
    if (stepIndex === 0 && activeStep === 1) {
      setActiveStep(0);
    } else if (stepIndex === 1 && activeStep === 0) {
      // Check if step 0 is complete before allowing navigation
      if (form.values.firstName && form.values.lastName && form.values.email) {
        setActiveStep(1);
      }
    }
  };

  const isNextDisabled = () => {
    if (activeStep === 0) {
      return (
        !form.values.firstName ||
        !form.values.lastName ||
        !form.values.email ||
        Boolean(form.errors.email) ||
        Boolean(form.errors.phone)
      );
    } else {
      return (
        !form.values.address ||
        addressNeedsConfirmation ||
        !form.values.serviceTime ||
        form.values.serviceTime === 0 ||
        Boolean(form.errors.serviceTime)
      );
    }
  };

  const styles = useStyles();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Create Customer
        </Typography>
        <CustomStepper
          steps={steps}
          activeStep={activeStep}
          onStepClick={handleStepClick}
        ></CustomStepper>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.formContainer}>
          {activeStep === 0 && (
            <Box sx={styles.fieldRow}>
              <Typography>First Name</Typography>
              <TextField
                name="firstName"
                value={form.values.firstName}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.firstName && Boolean(form.errors.firstName)}
                helperText={form.touched.firstName && form.errors.firstName}
                margin="dense"
                variant="outlined"
                fullWidth
              />
              <Typography>Last Name</Typography>
              <TextField
                name="lastName"
                variant="outlined"
                fullWidth
                margin="dense"
                value={form.values.lastName}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.lastName && Boolean(form.errors.lastName)}
                helperText={form.touched.lastName && form.errors.lastName}
              />
              <Typography>Email</Typography>
              <TextField
                name="email"
                type="email"
                variant="outlined"
                margin="dense"
                fullWidth
                value={form.values.email}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.email && Boolean(form.errors.email)}
                helperText={form.touched.email && form.errors.email}
              />
              <Typography>Phone Number</Typography>
              <TextField
                name="phone"
                type="tel"
                variant="outlined"
                fullWidth
                margin="dense"
                value={form.values.phone}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.phone && Boolean(form.errors.phone)}
                helperText={form.touched.phone && form.errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+</InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={styles.fieldRow}>
              <Typography>Address</Typography>
              <Box>
                {addressNeedsConfirmation && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: "0.875rem",
                      marginBottom: "8px",
                    }}
                  >
                    Confirm Address Suggestion
                  </Typography>
                )}
                <Autocomplete
                  freeSolo
                  filterOptions={(x) => x}
                  open={autocompleteOpen}
                  onOpen={() => setAutocompleteOpen(true)}
                  onClose={() => setAutocompleteOpen(false)}
                  value={form.values.address}
                  options={placePredictions.map((prediction) => ({
                    label: prediction.description,
                    value: prediction,
                  }))}
                  onChange={(_evt, value) => {
                    if (value && typeof value === "object") {
                      form.setFieldValue("address", String(value.label));
                      setAddressNeedsConfirmation(false);
                      setAutocompleteOpen(false);
                    } else if (typeof value === "string") {
                      form.setFieldValue("address", value);
                      setAddressNeedsConfirmation(true);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      margin="dense"
                      error={
                        addressNeedsConfirmation ||
                        (form.touched.address && Boolean(form.errors.address))
                      }
                      helperText={form.touched.address && form.errors.address}
                      onChange={(evt) => {
                        const inputValue = evt.target.value?.trim();
                        form.setFieldValue("address", inputValue);
                        form.setFieldTouched("address", true);
                        if (inputValue) {
                          getPlacePredictions({ input: inputValue });
                        } else {
                          setAutocompleteOpen(false);
                        }
                        // Reset confirmation state when user types
                        setAddressNeedsConfirmation(true);
                      }}
                      {...params}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: addressNeedsConfirmation
                              ? theme.palette.error.main
                              : undefined,
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Typography>Service Duration</Typography>
              <TextField
                name="serviceTime"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                value={form.values.serviceTime}
                onChange={(e) => {
                  const value = e.target.value;
                  form.setFieldValue(
                    "serviceTime",
                    value === "" ? "" : Number(value),
                  );
                }}
                onBlur={form.handleBlur}
                error={
                  form.touched.serviceTime && Boolean(form.errors.serviceTime)
                }
                helperText={form.touched.serviceTime && form.errors.serviceTime}
                // Input guards only (no typed "-"/"e"/letters, no stepping).
                // The value stays `number | ""`, which this form's schema
                // preprocesses ("" -> 0) to raise the ">= 1" message.
                inputProps={numericInputProps({ allowDecimal: true })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">minutes</InputAdornment>
                  ),
                }}
                sx={numericFieldSx}
              />
              <Typography>Notes</Typography>
              <TextField
                name="notes"
                variant="outlined"
                fullWidth
                margin="dense"
                multiline
                rows={3}
                value={form.values.notes}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.notes && Boolean(form.errors.notes)}
                helperText={form.touched.notes && form.errors.notes}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            variant="text"
            sx={{ marginLeft: "-16px" }}
          >
            Back
          </Button>
        )}
        <Box sx={styles.dialogActionsRight}>
          <Button onClick={handleClose} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={isNextDisabled()}
          >
            {activeStep === steps.length - 1 ? "Create Customer" : "Continue"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeSxStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "12px",
      maxWidth: "600px",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogActions: {
    gap: "12px",
    display: "flex",
    justifyContent: "space-between",
    padding: "0px",
    paddingTop: "24px",
  },
  stepper: {
    "& .MuiStepLabel-root": {
      "& .MuiStepLabel-labelContainer": {
        "& .MuiStepLabel-label": {
          fontSize: "14px",
          fontWeight: 500,
        },
      },
    },
    "& .MuiStepIcon-root": {
      fontSize: "24px",
      "&.Mui-completed": {
        color: theme.palette.success.main,
      },
      "&.Mui-active": {
        color: theme.palette.primary.main,
      },
    },
  },
  field: {
    marginBottom: "24px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
  },
  fieldRow: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    alignItems: "center",
  },
  dialogActionsRight: {
    marginLeft: "auto",
    display: "flex",
    gap: "16px",
  },
}));

export default AddCustomerModal;
